import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { AdminPageHeader, StateBlock, StatusBadge } from "../components/AdminUi";
import { BRANDS, socialApi } from "./socialMarketingApi";

const EMPTY_ACCOUNT = {
  enabled: false,
  appId: "",
  appSecret: "",
  pageId: "",
  pageToken: "",
  instagramId: "",
};

function emptyAccounts() {
  return Object.fromEntries(Object.keys(BRANDS).map((brand) => [brand, { ...EMPTY_ACCOUNT }]));
}

function fromApi(data) {
  const accounts = emptyAccounts();
  (data.accounts || []).forEach((row) => {
    accounts[row.brand] = {
      ...EMPTY_ACCOUNT,
      enabled: Boolean(row.enabled),
      appId: row.app_id || "",
      pageId: row.facebook_page_id || "",
      instagramId: row.instagram_account_id || "",
    };
  });
  return accounts;
}

export default function SocialMarketingMetaModule() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [accounts, setAccounts] = useState(emptyAccounts);
  const [saved, setSaved] = useState(null);
  const [checks, setChecks] = useState([]);
  const [busy, setBusy] = useState("");

  const apply = (data) => {
    setAppId(data.app_id || "");
    setAccounts(fromApi(data));
    setSaved(data);
    setAppSecret("");
  };

  const load = useCallback(async () => {
    setError("");
    const data = await socialApi("/meta");
    apply(data);
    return data;
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    socialApi("/meta")
      .then((data) => { if (active) apply(data); })
      .catch((err) => { if (active) setError(err.message || "Impossibile caricare il collegamento Meta"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (err) {
      toast.error(err.message || "Operazione non riuscita");
    } finally {
      setBusy("");
    }
  };

  const payload = () => {
    const body = {
      app_id: appId.trim(),
      accounts: {},
    };
    if (appSecret.trim()) body.app_secret = appSecret.trim();
    Object.entries(accounts).forEach(([brand, row]) => {
      const account = {
        enabled: Boolean(row.enabled),
        app_id: row.appId.trim(),
        facebook_page_id: row.pageId.trim(),
        instagram_account_id: row.instagramId.trim(),
      };
      if (row.appSecret.trim()) account.app_secret = row.appSecret.trim();
      if (row.pageToken.trim()) account.facebook_page_token = row.pageToken.trim();
      body.accounts[brand] = account;
    });
    return body;
  };

  const persist = async () => {
    const data = await socialApi("/meta", { method: "PUT", body: JSON.stringify(payload()) });
    apply(data);
    return data;
  };

  const save = () => run("save", async () => {
    await persist();
    toast.success("Collegamento Meta salvato");
  });

  const test = () => run("test", async () => {
    await persist();
    const data = await socialApi("/meta/test", { method: "POST" });
    setChecks(data.results || []);
    if (data.ok) toast.success("Collegamento verificato");
    else toast.error("Uno o più collegamenti non rispondono");
  });

  const patchAccount = (brand, patch) => {
    setAccounts((current) => ({ ...current, [brand]: { ...current[brand], ...patch } }));
  };

  const savedAccount = (brand) => (saved?.accounts || []).find((row) => row.brand === brand);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Collegamento Meta"
        description="Un'unica app Meta per Affidya, Casadiriposoperte e R&CO Group. App ID e App Secret generali valgono per tutti i marchi; un valore sul singolo marchio li sostituisce. Con il collegamento attivo, l'approvazione di un post pubblica su Facebook e Instagram all'orario indicato."
      />
      {loading ? <StateBlock state="loading" title="Caricamento del collegamento…" /> : null}
      {error ? <StateBlock state="error" title="Impossibile caricare il collegamento Meta" description={error} onRetry={() => load().then(() => setError("")).catch((err) => setError(err.message))} /> : null}

      {!loading && !error ? (
        <>
          <Card>
            <CardContent className="space-y-4 p-6">
              <div>
                <h2 className="text-base font-medium text-admin-text">App Meta</h2>
                <p className="mt-1 text-sm text-admin-muted">
                  Questi dati valgono per i tre marchi. Si compilano quando l'app Meta è pronta. Un App ID o un App Secret inseriti nel marchio qui sotto sostituiscono questi.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="meta-app-id">App ID</Label>
                <Input id="meta-app-id" value={appId} onChange={(event) => setAppId(event.target.value)} inputMode="numeric" autoComplete="off" placeholder="ID numerico dell'app Meta" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="meta-app-secret">App Secret</Label>
                <Input
                  id="meta-app-secret"
                  type="password"
                  autoComplete="off"
                  value={appSecret}
                  onChange={(event) => setAppSecret(event.target.value)}
                  placeholder={saved?.app_secret_set ? `Salvato (${saved.app_secret_preview}). Scrivi solo per sostituirlo.` : "Incolla l'App Secret"}
                />
              </div>
              {saved?.app_secret_set ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => run("clear-secret", async () => {
                    const data = await socialApi("/meta", { method: "PUT", body: JSON.stringify({ app_id: appId.trim(), clear_app_secret: true, accounts: {} }) });
                    apply(data);
                    toast.success("App Secret generale rimosso");
                  })}
                >
                  Rimuovi App Secret generale
                </Button>
              ) : null}
            </CardContent>
          </Card>

          {Object.values(BRANDS).map((brand) => {
            const row = accounts[brand.id] || EMPTY_ACCOUNT;
            const known = savedAccount(brand.id);
            const active = Boolean(known?.active);
            return (
              <Card key={brand.id}>
                <CardContent className="space-y-4 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-base font-medium text-admin-text">{brand.label}</h2>
                    <StatusBadge tone={active ? "success" : known?.enabled ? "warning" : "neutral"}>
                      {active ? "Collegamento attivo" : known?.enabled ? "Credenziali incomplete" : "Collegamento spento"}
                    </StatusBadge>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-admin-text">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(event) => patchAccount(brand.id, { enabled: event.target.checked })}
                    />
                    Collegamento attivo
                  </label>
                  <p className="text-sm text-admin-muted">
                    Con il collegamento attivo, approvare un post di {brand.label} che include Facebook o Instagram lo pubblica su quei canali all'orario indicato, dopo un messaggio di conferma.
                    {known?.resolved_app_id ? ` App ID in uso: ${known.resolved_app_id}${known.uses_global_app_id ? " (generale)" : " (di questo marchio)"}.` : ""}
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor={`${brand.id}-app-id`}>App ID di questo marchio</Label>
                      <Input id={`${brand.id}-app-id`} value={row.appId} onChange={(event) => patchAccount(brand.id, { appId: event.target.value })} inputMode="numeric" autoComplete="off" placeholder="Vuoto: usa l'App ID generale" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`${brand.id}-app-secret`}>App Secret di questo marchio</Label>
                      <Input
                        id={`${brand.id}-app-secret`}
                        type="password"
                        autoComplete="off"
                        value={row.appSecret}
                        onChange={(event) => patchAccount(brand.id, { appSecret: event.target.value })}
                        placeholder={known?.app_secret_set ? `Salvato (${known.app_secret_preview}). Sostituisce quello generale.` : "Vuoto: usa l'App Secret generale"}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`${brand.id}-page-id`}>ID Pagina Facebook</Label>
                      <Input id={`${brand.id}-page-id`} value={row.pageId} onChange={(event) => patchAccount(brand.id, { pageId: event.target.value })} inputMode="numeric" autoComplete="off" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`${brand.id}-page-token`}>Token della pagina Facebook</Label>
                      <Input
                        id={`${brand.id}-page-token`}
                        type="password"
                        autoComplete="off"
                        value={row.pageToken}
                        onChange={(event) => patchAccount(brand.id, { pageToken: event.target.value })}
                        placeholder={known?.facebook_page_token_set ? `Salvato (${known.facebook_page_token_preview}). Scrivi solo per sostituirlo.` : "Token della pagina"}
                      />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label htmlFor={`${brand.id}-ig`}>ID account Instagram</Label>
                      <Input id={`${brand.id}-ig`} value={row.instagramId} onChange={(event) => patchAccount(brand.id, { instagramId: event.target.value })} inputMode="numeric" autoComplete="off" />
                      <p className="text-xs text-admin-muted">È l'account Instagram collegato alla pagina. La pubblicazione usa il token della pagina.</p>
                    </div>
                  </div>
                  {known?.app_secret_set || known?.facebook_page_token_set ? (
                    <div className="flex flex-wrap gap-2">
                      {known?.app_secret_set ? (
                        <Button type="button" variant="outline" onClick={() => run(`clear-secret-${brand.id}`, async () => {
                          const data = await socialApi("/meta", {
                            method: "PUT",
                            body: JSON.stringify({
                              app_id: appId.trim(),
                              accounts: { [brand.id]: { enabled: row.enabled, app_id: row.appId.trim(), facebook_page_id: row.pageId.trim(), instagram_account_id: row.instagramId.trim(), clear_app_secret: true } },
                            }),
                          });
                          apply(data);
                          toast.success(`App Secret di ${brand.label} rimosso. Vale di nuovo quello generale.`);
                        })}
                        >
                          Rimuovi App Secret del marchio
                        </Button>
                      ) : null}
                      {known?.facebook_page_token_set ? (
                        <Button type="button" variant="outline" onClick={() => run(`clear-token-${brand.id}`, async () => {
                          const data = await socialApi("/meta", {
                            method: "PUT",
                            body: JSON.stringify({
                              app_id: appId.trim(),
                              accounts: { [brand.id]: { enabled: row.enabled, app_id: row.appId.trim(), facebook_page_id: row.pageId.trim(), instagram_account_id: row.instagramId.trim(), clear_facebook_page_token: true } },
                            }),
                          });
                          apply(data);
                          toast.success(`Token della pagina di ${brand.label} rimosso`);
                        })}
                        >
                          Rimuovi token della pagina
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}

          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={save} disabled={busy === "save" || busy === "test"}>Salva</Button>
            <Button type="button" variant="outline" onClick={test} disabled={busy === "test" || busy === "save"}>Verifica collegamento</Button>
          </div>
          {checks.length ? (
            <Card>
              <CardContent className="space-y-3 p-6">
                <h2 className="text-base font-medium text-admin-text">Esito della verifica</h2>
                {checks.map((item) => (
                  <div key={item.brand} className="text-sm text-admin-text">
                    <span className="font-medium">{item.label}</span>
                    {item.ok ? (
                      <span className="text-admin-muted">
                        {item.facebook?.name ? ` · Pagina ${item.facebook.name}` : ""}
                        {item.instagram?.username ? ` · Instagram @${item.instagram.username}` : ""}
                      </span>
                    ) : (
                      <span> · {item.error || "Verifica non riuscita"}</span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
